/**
 * PlatformPermissionsChecklist.jsx
 * 
 * COMPLIANCE NOTES:
 * - TikTok: Requires PER-POST consent (TikTok API guidelines mandate this)
 *   We save default preferences but user must still confirm each post
 * - Pinterest: Allows one-time default board selection
 * 
 * This checklist captures DEFAULT preferences to pre-fill the per-post settings.
 * For TikTok in Auto-Pilot mode, we notify users before each post.
 * 
 * Usage:
 * <PlatformPermissionsChecklist 
 *   selectedPlatforms={["tiktok", "pinterest"]}
 *   onPermissionsComplete={(platforms) => setPermissionsGranted(platforms)}
 *   mode="autopilot" // or "single" or "bulk"
 * />
 */

import { useState, useEffect } from "react";
import { apiClient } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { 
  Check, 
  ChevronDown, 
  ChevronRight, 
  Shield, 
  Info,
  Settings,
  Loader2,
  Eye,
  EyeOff,
  MessageCircle,
  Users,
  Scissors,
  Tag,
  Building2,
  Bell,
  AlertTriangle
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

// TikTok privacy level options - NO default per TikTok guidelines
const TIKTOK_PRIVACY_OPTIONS = [
  { value: "PUBLIC_TO_EVERYONE", label: "Public", description: "Anyone can view" },
  { value: "MUTUAL_FOLLOW_FRIENDS", label: "Friends", description: "Only mutual followers" },
  { value: "FOLLOWER_OF_CREATOR", label: "Followers", description: "Only your followers" },
  { value: "SELF_ONLY", label: "Private", description: "Only you can view" }
];

export default function PlatformPermissionsChecklist({ 
  selectedPlatforms = [], 
  onPermissionsComplete,
  compact = false,  // For inline use in AutoPilot setup
  mode = "single"   // "single", "bulk", or "autopilot"
}) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedPlatform, setExpandedPlatform] = useState(null);
  
  // Track which platforms need permissions
  const [platformsNeedingConsent, setPlatformsNeedingConsent] = useState([]);
  
  // TikTok settings state
  const [tiktokSettings, setTiktokSettings] = useState({
    default_privacy_level: "",
    allow_comment: true,
    allow_duet: false,
    allow_stitch: false,
    disclosure_enabled: false,
    is_your_brand: false,
    is_branded_content: false,
    auto_posting_consent: false
  });
  
  // Pinterest settings state
  const [pinterestSettings, setPinterestSettings] = useState({
    default_board_id: "",
    default_board_name: "",
    auto_posting_consent: false
  });
  
  const [pinterestBoards, setPinterestBoards] = useState([]);
  
  // Check if TikTok or Pinterest is selected
  const isTikTokSelected = selectedPlatforms.includes("tiktok");
  const isPinterestSelected = selectedPlatforms.includes("pinterest");
  
  // Fetch existing settings on mount
  useEffect(() => {
    const fetchSettings = async () => {
      setLoading(true);
      const needsConsent = [];
      
      try {
        if (isTikTokSelected) {
          const ttRes = await apiClient.get("/social/tiktok/auto-posting-settings");
          if (ttRes.data) {
            setTiktokSettings({
              default_privacy_level: ttRes.data.default_privacy_level || "",
              allow_comment: ttRes.data.allow_comment ?? true,
              allow_duet: ttRes.data.allow_duet ?? false,
              allow_stitch: ttRes.data.allow_stitch ?? false,
              disclosure_enabled: ttRes.data.disclosure_enabled ?? false,
              is_your_brand: ttRes.data.is_your_brand ?? false,
              is_branded_content: ttRes.data.is_branded_content ?? false,
              auto_posting_consent: ttRes.data.auto_posting_consent ?? false
            });
            
            // If no consent yet, this platform needs permissions
            if (!ttRes.data.auto_posting_consent) {
              needsConsent.push("tiktok");
            }
          } else {
            needsConsent.push("tiktok");
          }
        }
        
        if (isPinterestSelected) {
          const pinRes = await apiClient.get("/social/pinterest/auto-posting-settings");
          if (pinRes.data) {
            setPinterestSettings({
              default_board_id: pinRes.data.default_board_id || "",
              default_board_name: pinRes.data.default_board_name || "",
              auto_posting_consent: pinRes.data.auto_posting_consent ?? false
            });
            
            if (!pinRes.data.auto_posting_consent) {
              needsConsent.push("pinterest");
            }
          } else {
            needsConsent.push("pinterest");
          }
          
          // Fetch Pinterest boards
          try {
            const boardsRes = await apiClient.get("/social/pinterest/boards");
            if (boardsRes.data?.boards) {
              setPinterestBoards(boardsRes.data.boards);
            }
          } catch (e) {
            // Pinterest not connected or boards unavailable
          }
        }
        
        setPlatformsNeedingConsent(needsConsent);
        
        // Auto-expand first platform that needs consent
        if (needsConsent.length > 0) {
          setExpandedPlatform(needsConsent[0]);
        }
        
        // If no platforms need consent, notify parent immediately
        if (needsConsent.length === 0) {
          onPermissionsComplete?.([...selectedPlatforms.filter(p => p === "tiktok" || p === "pinterest")]);
        }
        
      } catch (error) {
        console.error("Failed to fetch platform settings:", error);
      } finally {
        setLoading(false);
      }
    };
    
    if (isTikTokSelected || isPinterestSelected) {
      fetchSettings();
    } else {
      setLoading(false);
      setPlatformsNeedingConsent([]);
    }
  }, [isTikTokSelected, isPinterestSelected, selectedPlatforms]);
  
  // Save TikTok settings
  const saveTiktokSettings = async () => {
    if (!tiktokSettings.default_privacy_level) {
      toast.error("Please select a privacy level for TikTok");
      return false;
    }
    
    setSaving(true);
    try {
      await apiClient.put("/social/tiktok/auto-posting-settings", {
        ...tiktokSettings,
        auto_posting_consent: true
      });
      
      setTiktokSettings(prev => ({ ...prev, auto_posting_consent: true }));
      setPlatformsNeedingConsent(prev => prev.filter(p => p !== "tiktok"));
      
      toast.success("TikTok preferences saved!", {
        description: "You can change these anytime in Settings → Notifications & Permissions"
      });
      
      return true;
    } catch (error) {
      toast.error("Failed to save TikTok settings");
      return false;
    } finally {
      setSaving(false);
    }
  };
  
  // Save Pinterest settings
  const savePinterestSettings = async () => {
    setSaving(true);
    try {
      await apiClient.put("/social/pinterest/auto-posting-settings", {
        ...pinterestSettings,
        auto_posting_consent: true
      });
      
      setPinterestSettings(prev => ({ ...prev, auto_posting_consent: true }));
      setPlatformsNeedingConsent(prev => prev.filter(p => p !== "pinterest"));
      
      toast.success("Pinterest preferences saved!", {
        description: "You can change these anytime in Settings → Notifications & Permissions"
      });
      
      return true;
    } catch (error) {
      toast.error("Failed to save Pinterest settings");
      return false;
    } finally {
      setSaving(false);
    }
  };
  
  // Save all and notify parent
  const saveAllAndContinue = async () => {
    let allSaved = true;
    
    if (platformsNeedingConsent.includes("tiktok")) {
      const ttSaved = await saveTiktokSettings();
      if (!ttSaved) allSaved = false;
    }
    
    if (platformsNeedingConsent.includes("pinterest")) {
      const pinSaved = await savePinterestSettings();
      if (!pinSaved) allSaved = false;
    }
    
    if (allSaved) {
      onPermissionsComplete?.(["tiktok", "pinterest"].filter(p => selectedPlatforms.includes(p)));
    }
  };
  
  // If nothing needs consent, render nothing
  if (loading) {
    return (
      <div className="p-4 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin text-viraly-orange" />
        <span className="text-sm text-viraly-muted">Checking platform permissions...</span>
      </div>
    );
  }
  
  if (platformsNeedingConsent.length === 0) {
    return null; // Already consented - don't show anything
  }
  
  const renderTikTokSettings = () => (
    <div className="space-y-4 p-4 bg-black/20 rounded-lg">
      {/* Privacy Level - Required, NO default */}
      <div>
        <label className="block text-sm font-medium text-viraly-light mb-2 flex items-center gap-2">
          <Eye className="w-4 h-4" />
          Default Privacy Level <span className="text-red-400">*</span>
        </label>
        <p className="text-xs text-viraly-muted mb-2">Who can view your TikTok posts by default</p>
        <div className="grid grid-cols-2 gap-2">
          {TIKTOK_PRIVACY_OPTIONS.map(option => (
            <button
              key={option.value}
              onClick={() => setTiktokSettings(prev => ({ ...prev, default_privacy_level: option.value }))}
              className={`p-3 rounded-lg border text-left transition-all ${
                tiktokSettings.default_privacy_level === option.value
                  ? "border-viraly-orange bg-viraly-orange/10"
                  : "border-white/10 hover:border-white/20"
              }`}
            >
              <p className="font-medium text-sm">{option.label}</p>
              <p className="text-xs text-viraly-muted">{option.description}</p>
            </button>
          ))}
        </div>
      </div>
      
      {/* Interaction Toggles */}
      <div>
        <label className="block text-sm font-medium text-viraly-light mb-2 flex items-center gap-2">
          <MessageCircle className="w-4 h-4" />
          Interaction Settings
        </label>
        <p className="text-xs text-viraly-muted mb-2">Allow viewers to interact with your posts</p>
        <div className="space-y-2">
          <label className="flex items-center justify-between p-3 bg-white/5 rounded-lg cursor-pointer hover:bg-white/10">
            <div className="flex items-center gap-2">
              <MessageCircle className="w-4 h-4 text-blue-400" />
              <span className="text-sm">Allow Comments</span>
            </div>
            <input
              type="checkbox"
              checked={tiktokSettings.allow_comment}
              onChange={(e) => setTiktokSettings(prev => ({ ...prev, allow_comment: e.target.checked }))}
              className="w-4 h-4 rounded border-white/20 bg-white/10 text-viraly-orange focus:ring-viraly-orange"
            />
          </label>
          <label className="flex items-center justify-between p-3 bg-white/5 rounded-lg cursor-pointer hover:bg-white/10">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-purple-400" />
              <span className="text-sm">Allow Duets</span>
            </div>
            <input
              type="checkbox"
              checked={tiktokSettings.allow_duet}
              onChange={(e) => setTiktokSettings(prev => ({ ...prev, allow_duet: e.target.checked }))}
              className="w-4 h-4 rounded border-white/20 bg-white/10 text-viraly-orange focus:ring-viraly-orange"
            />
          </label>
          <label className="flex items-center justify-between p-3 bg-white/5 rounded-lg cursor-pointer hover:bg-white/10">
            <div className="flex items-center gap-2">
              <Scissors className="w-4 h-4 text-green-400" />
              <span className="text-sm">Allow Stitches</span>
            </div>
            <input
              type="checkbox"
              checked={tiktokSettings.allow_stitch}
              onChange={(e) => setTiktokSettings(prev => ({ ...prev, allow_stitch: e.target.checked }))}
              className="w-4 h-4 rounded border-white/20 bg-white/10 text-viraly-orange focus:ring-viraly-orange"
            />
          </label>
        </div>
      </div>
      
      {/* Commercial Content Disclosure */}
      <div>
        <label className="block text-sm font-medium text-viraly-light mb-2 flex items-center gap-2">
          <Tag className="w-4 h-4" />
          Commercial Content Disclosure
        </label>
        <p className="text-xs text-viraly-muted mb-2">Required if you're promoting products or services</p>
        
        <label className="flex items-center justify-between p-3 bg-white/5 rounded-lg cursor-pointer hover:bg-white/10 mb-2">
          <div>
            <span className="text-sm">Enable Commercial Disclosure</span>
            <p className="text-xs text-viraly-muted">Turn on if posts promote your business</p>
          </div>
          <input
            type="checkbox"
            checked={tiktokSettings.disclosure_enabled}
            onChange={(e) => setTiktokSettings(prev => ({ 
              ...prev, 
              disclosure_enabled: e.target.checked,
              is_your_brand: e.target.checked ? prev.is_your_brand : false,
              is_branded_content: e.target.checked ? prev.is_branded_content : false
            }))}
            className="w-4 h-4 rounded border-white/20 bg-white/10 text-viraly-orange focus:ring-viraly-orange"
          />
        </label>
        
        {tiktokSettings.disclosure_enabled && (
          <div className="ml-4 space-y-2 pt-2 border-t border-white/10">
            <label className="flex items-center gap-2 p-2 cursor-pointer">
              <input
                type="checkbox"
                checked={tiktokSettings.is_your_brand}
                onChange={(e) => setTiktokSettings(prev => ({ ...prev, is_your_brand: e.target.checked }))}
                className="w-4 h-4 rounded border-white/20 bg-white/10 text-viraly-orange focus:ring-viraly-orange"
              />
              <div>
                <span className="text-sm">Your Brand</span>
                <p className="text-xs text-viraly-muted">Promoting your own products/services</p>
              </div>
            </label>
            <label className="flex items-center gap-2 p-2 cursor-pointer">
              <input
                type="checkbox"
                checked={tiktokSettings.is_branded_content}
                onChange={(e) => setTiktokSettings(prev => ({ ...prev, is_branded_content: e.target.checked }))}
                className="w-4 h-4 rounded border-white/20 bg-white/10 text-viraly-orange focus:ring-viraly-orange"
              />
              <div>
                <span className="text-sm">Branded Content</span>
                <p className="text-xs text-viraly-muted">Sponsored by another brand</p>
              </div>
            </label>
            
            {tiktokSettings.is_branded_content && tiktokSettings.default_privacy_level === "SELF_ONLY" && (
              <p className="text-xs text-amber-400 p-2 bg-amber-500/10 rounded">
                Note: Branded content cannot be set to Private visibility
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
  
  const renderPinterestSettings = () => (
    <div className="space-y-4 p-4 bg-black/20 rounded-lg">
      {/* Default Board Selection */}
      <div>
        <label className="block text-sm font-medium text-viraly-light mb-2 flex items-center gap-2">
          <Building2 className="w-4 h-4" />
          Default Board
        </label>
        <p className="text-xs text-viraly-muted mb-2">Select a board where your pins will be saved by default</p>
        
        {pinterestBoards.length > 0 ? (
          <select
            value={pinterestSettings.default_board_id}
            onChange={(e) => {
              const board = pinterestBoards.find(b => b.id === e.target.value);
              setPinterestSettings(prev => ({
                ...prev,
                default_board_id: e.target.value,
                default_board_name: board?.name || ""
              }));
            }}
            className="w-full p-3 bg-white/5 border border-white/10 rounded-lg text-viraly-light focus:border-viraly-orange focus:outline-none"
          >
            <option value="">Select a board...</option>
            {pinterestBoards.map(board => (
              <option key={board.id} value={board.id}>{board.name}</option>
            ))}
          </select>
        ) : (
          <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-sm text-amber-400">
            No Pinterest boards found. Make sure your Pinterest account is connected and has at least one board.
          </div>
        )}
      </div>
    </div>
  );
  
  return (
    <div className={`rounded-xl border border-viraly-orange/30 bg-gradient-to-br from-viraly-orange/5 to-transparent overflow-hidden ${compact ? "" : "p-1"}`}>
      {/* Header */}
      <div className="p-4 border-b border-white/10 bg-viraly-orange/10">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-viraly-orange/20 flex items-center justify-center flex-shrink-0">
            <Shield className="w-5 h-5 text-viraly-orange" />
          </div>
          <div>
            <h3 className="font-semibold text-viraly-light">Platform Permissions Required</h3>
            <p className="text-sm text-viraly-muted mt-1">
              {platformsNeedingConsent.length === 1 
                ? `${platformsNeedingConsent[0] === "tiktok" ? "TikTok" : "Pinterest"} requires some preferences before posting.`
                : "TikTok and Pinterest require some preferences before posting."}
            </p>
            
            {/* TikTok Per-Post Consent Notice for Auto-Pilot */}
            {platformsNeedingConsent.includes("tiktok") && (mode === "autopilot" || mode === "bulk") && (
              <div className="mt-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <div className="flex items-start gap-2">
                  <Bell className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-amber-400 font-medium">TikTok requires your approval for each post</p>
                    <p className="text-xs text-amber-400/80 mt-1">
                      Per TikTok's API guidelines, we'll notify you before each post is published. 
                      You'll receive a notification to approve the privacy and settings. 
                      This is TikTok's requirement, not ours.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Pinterest one-time notice */}
            {platformsNeedingConsent.includes("pinterest") && !platformsNeedingConsent.includes("tiktok") && (
              <p className="text-xs text-viraly-orange mt-2 flex items-center gap-1">
                <Info className="w-3 h-3" />
                One-time setup • Saved in Settings → Notifications & Permissions
              </p>
            )}
            
            {/* Mixed notice when both are selected */}
            {platformsNeedingConsent.includes("pinterest") && platformsNeedingConsent.includes("tiktok") && (
              <p className="text-xs text-viraly-muted mt-2 flex items-center gap-1">
                <Info className="w-3 h-3" />
                Pinterest: One-time setup • TikTok: Per-post approval required
              </p>
            )}
          </div>
        </div>
      </div>
      
      {/* Platform Sections */}
      <div className="p-4 space-y-3">
        {/* TikTok Section */}
        {platformsNeedingConsent.includes("tiktok") && (
          <div className="rounded-lg border border-white/10 overflow-hidden">
            <button
              onClick={() => setExpandedPlatform(expandedPlatform === "tiktok" ? null : "tiktok")}
              className="w-full p-3 flex items-center justify-between bg-black/20 hover:bg-black/30 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-black flex items-center justify-center">
                  <span className="text-xs font-bold text-white">TT</span>
                </div>
                <div className="text-left">
                  <p className="font-medium text-sm">TikTok Default Settings</p>
                  <p className="text-xs text-viraly-muted">These will pre-fill your per-post settings</p>
                </div>
              </div>
              {expandedPlatform === "tiktok" ? (
                <ChevronDown className="w-4 h-4 text-viraly-muted" />
              ) : (
                <ChevronRight className="w-4 h-4 text-viraly-muted" />
              )}
            </button>
            
            <AnimatePresence>
              {expandedPlatform === "tiktok" && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {renderTikTokSettings()}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
        
        {/* Pinterest Section */}
        {platformsNeedingConsent.includes("pinterest") && (
          <div className="rounded-lg border border-white/10 overflow-hidden">
            <button
              onClick={() => setExpandedPlatform(expandedPlatform === "pinterest" ? null : "pinterest")}
              className="w-full p-3 flex items-center justify-between bg-black/20 hover:bg-black/30 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-red-600 flex items-center justify-center">
                  <span className="text-xs font-bold text-white">P</span>
                </div>
                <div className="text-left">
                  <p className="font-medium text-sm">Pinterest Settings</p>
                  <p className="text-xs text-viraly-muted">Default board selection</p>
                </div>
              </div>
              {expandedPlatform === "pinterest" ? (
                <ChevronDown className="w-4 h-4 text-viraly-muted" />
              ) : (
                <ChevronRight className="w-4 h-4 text-viraly-muted" />
              )}
            </button>
            
            <AnimatePresence>
              {expandedPlatform === "pinterest" && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {renderPinterestSettings()}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>
      
      {/* Save Button */}
      <div className="p-4 border-t border-white/10 bg-black/20">
        <Button
          onClick={saveAllAndContinue}
          disabled={saving || (platformsNeedingConsent.includes("tiktok") && !tiktokSettings.default_privacy_level)}
          className="w-full bg-viraly-orange hover:bg-viraly-orange/90"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Check className="w-4 h-4 mr-2" />
              Save & Continue
            </>
          )}
        </Button>
        {platformsNeedingConsent.includes("tiktok") && !tiktokSettings.default_privacy_level && (
          <p className="text-xs text-amber-400 mt-2 text-center">
            Please select a TikTok privacy level to continue
          </p>
        )}
      </div>
    </div>
  );
}
